import { AxiosResponse } from "axios";
import { HttpService } from "@/core";
import { ResponseResp } from "@/core/interface";
import { message } from "ant-design-vue";
import ArrayToTree from "array-to-tree";
import {
  ProjectFundDetailResp,
  ProjectFundListRecord,
  ProjectFundListReq,
  ProjectFundListResp,
  ProjectFundReq,
  ProjectFundSimpleListResp,
  FileResp,
  Project,
} from "./project-fund.interface";

export class ProjectFundService {
  static async fetchList(params: ProjectFundListReq): Promise<ProjectFundListRecord[]> {
    let records: ProjectFundListRecord[] = [];
    try {
      const r: AxiosResponse<ResponseResp<ProjectFundListResp>> = await HttpService.axios({
        method: "GET",
        url: "/project_fund/full/list",
        params,
      });
      const res = r.data;
      if (res.respCode !== "0000") {
        message.error(res.respDesc);
        return records;
      }
      records = (res.data.records || []).map((item) => {
        if (item.hasChildren) {
          item.children = [];
        }
        return item;
      });
    } catch (e: any) {
      message.error(e.message);
    }
    return records;
  }

  static attachChildren(list, parentId, children) {
    const recurse = (list, children) => {
      return (list || []).map((item) => {
        if (item.id === parentId) {
          item.children = children || [];
        } else {
          if (item.children) {
            item.children = recurse(item.children, children);
          }
        }
        return item;
      });
    };
    return recurse(list, children);
  }

  static useFullListApi() {
    const api = (params) =>
      HttpService.axios({
        method: "GET",
        url: "/project_fund/full/list",
        params,
      });
    return HttpService.useRequest<ProjectFundListResp>(api);
  }

  static useSimpleListApi() {
    const api = () =>
      HttpService.axios({
        method: "GET",
        url: `/project_fund/simple/list`,
      });
    return HttpService.useRequest<ProjectFundSimpleListResp>(api, {
      formatResult: (resp) => {
        resp.records = ArrayToTree(resp.records || [], {
          parentProperty: "parentId",
          customID: "id",
        });
        return resp;
      },
    });
  }

  static useDetailApi() {
    const api = (id: string) =>
      HttpService.axios({
        method: "GET",
        url: `/project_fund/${id}`,
      });
    return HttpService.useRequest<ProjectFundDetailResp>(api);
  }

  static useCreateApi() {
    const api = (data: ProjectFundReq) =>
      HttpService.axios({
        method: "POST",
        url: "/project_fund",
        data,
      });
    return HttpService.useRequest<void>(api);
  }

  static useEditApi() {
    const api = (id: string, data: ProjectFundReq) =>
      HttpService.axios({
        method: "PUT",
        url: `/project_fund/${id}`,
        data,
      });
    return HttpService.useRequest<void>(api);
  }

  static useDeleteApi() {
    const api = (id: string) =>
      HttpService.axios({
        method: "DELETE",
        url: `/project_fund/${id}`,
      });
    return HttpService.useRequest<void>(api);
  }

  static useUploadProject() {
    const api = (url) =>
      HttpService.axios({
        url: `/project_fund/import/data/check?url=${url}`,
        data: {
          url,
        },
        method: "post",
      });
    return HttpService.useRequest<FileResp<Project>>(api);
  }

  static useFinishProject() {
    const api = (data) =>
      HttpService.axios({
        url: "/project_fund/excel/import",
        data,
        method: "post",
      });
    return HttpService.useRequest<void>(api);
  }
}
